import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { BrandMark } from "@/components/BrandMark";
import { HeartPulse, ArrowRight, SkipForward } from "lucide-react";
import { toast } from "sonner";

export default function Onboarding() {
  const [questions, setQuestions] = useState([]);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const { refresh } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/onboarding/questions").then(({ data }) => setQuestions(data.questions)).catch(() => {});
  }, []);

  if (!questions.length) {
    return <div className="flex min-h-screen items-center justify-center bg-background"><div className="h-10 w-10 animate-breathe rounded-full bg-primary" /></div>;
  }

  const current = questions[step];
  const isLast = step === questions.length - 1;

  const next = (skip) => {
    const updated = { ...answers };
    if (!skip && value.trim()) updated[current.key] = value.trim();
    setAnswers(updated);
    setValue("");
    if (isLast) return finish(updated);
    setStep(step + 1);
  };

  const finish = async (final) => {
    setSaving(true);
    try {
      await api.post("/onboarding/complete", { answers: final });
      await refresh();
      toast.success("Thank you for sharing. I'm glad you're here.");
      navigate("/app");
    } catch (e) {
      toast.error(apiErr(e));
      setSaving(false);
    }
  };

  return (
    <div className="grain flex min-h-screen flex-col bg-background px-5 py-10">
      <div className="mx-auto w-full max-w-xl flex-1 flex flex-col">
        <div className="mb-8 flex items-center gap-2">
          <BrandMark className="h-9 w-9" />
          <span className="font-serif text-lg font-semibold">EverMind</span>
        </div>

        <Progress value={((step + 1) / questions.length) * 100} className="mb-2 h-1.5" data-testid="onboarding-progress" />
        <p className="mb-8 text-xs text-muted-foreground">{t("onb.gentleChat")} · {t("onb.question")} {step + 1} {t("onb.of")} {questions.length}</p>

        <div className="flex-1">
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
                        transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}>
              <div className="flex gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/15">
                  <HeartPulse className="h-4 w-4 text-primary" strokeWidth={1.5} />
                </div>
                <p className="font-serif text-2xl leading-snug sm:text-3xl">{t(`onb.q.${current.key}`, current.prompt)}</p>
              </div>
              <Textarea
                autoFocus value={value} onChange={(e) => setValue(e.target.value)}
                placeholder={t("onb.answerPh")}
                data-testid="onboarding-answer-input"
                className="mt-6 min-h-32 resize-none rounded-2xl border-border/60 bg-card text-base leading-relaxed focus-visible:ring-primary"
              />
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-8 flex items-center justify-between">
          <Button variant="ghost" onClick={() => next(true)} disabled={saving} data-testid="onboarding-skip-button"
                  className="rounded-full text-muted-foreground gap-2">
            <SkipForward className="h-4 w-4" strokeWidth={1.5} /> {t("onb.skip")}
          </Button>
          <Button onClick={() => next(false)} disabled={saving} data-testid="onboarding-next-button" className="rounded-full px-6 gap-2">
            {isLast ? (saving ? t("onb.finishing") : t("onb.finish")) : t("onb.continue")}
            <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        </div>
        <p className="mt-6 text-center text-xs text-muted-foreground">{t("onb.footer")}</p>
      </div>
    </div>
  );
}
